import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsExporting from 'highcharts/modules/exporting';
import highchartsExportData from 'highcharts/modules/export-data';
import noDataToDisplay from 'highcharts/modules/no-data-to-display';
import { useAppContext } from '../../contexts/AppContext';
import { useEffect, useRef, useState } from 'react';
import { Backdrop, Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import { compare_name_asc } from '../../utils/utils';
dayjs.extend(dayOfYear);

highchartsExporting(Highcharts);
highchartsExportData(Highcharts);
noDataToDisplay(Highcharts);

type Props = {
  data: any;
};

const FinancialComparison = ({ data }: Props) => {
  const { isDarkMode } = useAppContext();
  const [chartOptions, setChartOptions] = useState<any>(null);
  const chartComponent = useRef(null);
  const [isLoading] = useState(false);

  useEffect(() => {
    prepareHighcharts(prepareData());
    //}
  }, [data]);

  const prepareData = () => {
    let opex: any[] = [];
    let capex: any[] = [];
    let cleaning_gains: any[] = [];
    let npv: any[] = [];

    data.sort(compare_name_asc).forEach((item: any) => {
      opex.push(Math.round((item?.opex?.total / 1000) * 100) / 100);
      capex.push(Math.round((item?.capex?.total / 1000) * 100) / 100);
      cleaning_gains.push(Math.round((item?.cleaning_gains / 1000) * 100) / 100);
      npv.push(Math.round((item?.net_present_value / 1000) * 100) / 100);
    });

    const maxNPV = Math.max(...npv);
    let timeseries_data = [
      {
        name: 'NPV CAPEX (KUSD)',
        data: capex,
      },
      {
        name: 'NPV OPEX (KUSD)',
        data: opex,
      },
      {
        name: 'NPV Cleaning Gains (KUSD)',
        data: cleaning_gains,
      },
      {
        name: 'NPV (KUSD)',
        data: npv,
        type: 'line',
        dataLabels: {
          enabled: true,
          useHTML: true,
          allowOverlap: true,
          formatter: function (this: any): any {
            if (this.point.y === maxNPV) {
              return '<span style="opacity: 1 !important;">🏆</span>';
            }
            return this.point.y;
          },
          style: {
            textAlign: 'center',
            color: '#000',
            fontSize: '24px',
            x: 0,
            y: -10,
          },
        },
      },
    ];
    return timeseries_data;
  };

  const prepareHighcharts = (highChartsData: any) => {
    const chart = {
      chart: {
        type: 'column',
      },
      title: {
        text: 'Financial Comparison',
        align: 'left',
      },
      xAxis: {
        categories: [...data.sort(compare_name_asc).map((item: any) => item.name)],
      },
      yAxis: [
        {
          title: {
            text: '(KUSD)',
          },
        },
      ],
      tooltip: {
        headerFormat: '<b>{point.x}</b><br/>',
        pointFormat: '{series.name}: {point.y}',
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: true,
          },
        },
      },
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
      },
      series: highChartsData,
    };
    setChartOptions(chart);
  };

  return (
    <>
      <Box sx={{ justifyContent: 'center', pl: 1, pr: 1 }}>
        {isLoading ? (
          <Backdrop open={true} sx={{ width: '100%', height: '450px', position: 'relative' }}>
            <CircularProgress />
          </Backdrop>
        ) : (
          data &&
          chartOptions && (
            <>
              <HighchartsReact
                ref={chartComponent}
                containerProps={{ className: isDarkMode ? 'dark-chart' : '', style: { width: '100%' } }}
                highcharts={Highcharts}
                options={chartOptions}
              />
            </>
          )
        )}
      </Box>
    </>
  );
};

export default FinancialComparison;
